/** Bind delegated/global events to replace inline onclick/onchange handlers. */

function invoke(name: string, ...args: any[]) {
  const fn = (window as any)[name];
  if (typeof fn === "function") {
    fn(...args);
  } else {
    console.warn(`[event-bindings] window.${name} is not a function`);
  }
}

function getActionTarget(e: Event): HTMLElement | null {
  return (e.target as HTMLElement).closest("[data-action]") as HTMLElement | null;
}

export function bindGlobalEvents(): void {
  // Cache clearing buttons
  document.querySelectorAll("[data-action='clearCache']").forEach((el) => {
    el.addEventListener("click", () => {
      const target = (el as HTMLElement).dataset.args || "all";
      invoke("clearCache", target);
    });
  });

  // Reference upload header changes
  const refUploadHdr = document.getElementById("ref-upload-hdr");
  if (refUploadHdr) {
    refUploadHdr.addEventListener("change", () => invoke("renderRefUploadPreview"));
  }

  // Reference upload modal
  bindClick("autoSelectRefColumns");
  bindClick("closeRefUploadModal");
  bindClick("confirmRefUpload");

  // Reference resync modal
  bindClick("closeRefResyncModal");
  bindClick("confirmRefResync");

  // Reference file edit modal
  bindClick("applyRefColumnChanges");
  bindClick("cancelRefColumnChanges");
  bindClick("toggleRefColumnsPanel");
  bindClick("closeRefFileEditModal");
  bindClick("saveRefFileEdits");

  // Reference reprocess modal
  bindClick("closeRefReprocessModal");
  bindClick("confirmRefReprocess");

  // Profile modal
  bindClick("loadUserDisplayColumns");
  bindClick("saveUserDisplayColumns");
  bindClick("closeProfileModal");
  bindClick("doChangePassword");

  // Delegated handlers for dynamic content
  document.body.addEventListener("change", (e) => {
    const el = getActionTarget(e);
    if (!el) return;
    const action = el.dataset.action;
    if (action === "updateColLabel") {
      invoke("updateColLabel", el.dataset.mapKey, el.dataset.key, (el as HTMLInputElement).value);
    } else if (action === "updateUserRole") {
      invoke("updateUserRole", el.dataset.username, (el as HTMLSelectElement).value);
    } else if (action === "toggleAttrVisibility") {
      invoke("toggleAttrVisibility", el.dataset.group, Number(el.dataset.attrId), (el as HTMLInputElement).checked);
    } else if (action === "onEditClassChange") {
      invoke("onEditClassChange", (el as HTMLSelectElement).value);
    } else if (action === "updateParserGroupField") {
      invoke("updateParserGroupField", el.dataset.groupKey, el.dataset.field, (el as HTMLInputElement).value);
    }
  });

  document.body.addEventListener("click", (e) => {
    const el = getActionTarget(e);
    if (!el) return;
    const action = el.dataset.action;
    if (action === "removeColLabel") {
      invoke("removeColLabel", el.dataset.mapKey, el.dataset.key);
    } else if (action === "resetUserPassword") {
      invoke("resetUserPassword", el.dataset.username);
    } else if (action === "deleteUser") {
      invoke("deleteUser", el.dataset.username);
    } else if (action === "addTitleAttr") {
      invoke("addTitleAttr", el.dataset.key);
    } else if (action === "moveTitleAttr") {
      invoke("moveTitleAttr", el.dataset.key, Number(el.dataset.dir));
    } else if (action === "removeTitleAttr") {
      invoke("removeTitleAttr", el.dataset.key);
    } else if (action === "addExtraEditAttr") {
      invoke("addExtraEditAttr");
    } else if (action === "editRefFile") {
      invoke("editRefFile", el.dataset.fileId, el.dataset.filename);
    } else if (action === "resyncRefFile") {
      invoke("resyncRefFile", el.dataset.fileId, el.dataset.filename);
    } else if (action === "openRefResyncModal") {
      invoke("openRefResyncModal", el.dataset.fileId, el.dataset.filename);
    } else if (action === "deleteRefFile") {
      invoke("deleteRefFile", el.dataset.fileId);
    } else if (action === "removeRefColumn") {
      invoke("removeRefColumn", el.dataset.col);
    } else if (action === "toggleReqDetails") {
      const target = el.classList.contains("req-toggle") ? el.parentElement : el;
      if (target) invoke("toggleReqDetails", target);
    } else if (action === "toggleSpecPanel") {
      invoke("toggleSpecPanel", Number(el.dataset.idx));
    } else if (action === "applySpecColumns") {
      invoke("applySpecColumns", Number(el.dataset.idx));
    } else if (action === "autoSpecColumns") {
      invoke("autoSpecColumns", Number(el.dataset.idx));
    } else if (action === "autoDetectGroupColumns") {
      invoke("autoDetectGroupColumns", Number(el.dataset.gidx));
    } else if (action === "syncParserGroup") {
      invoke("syncParserGroup", el.dataset.groupKey);
    } else if (action === "syncAllParserGroups") {
      invoke("syncAllParserGroups");
    } else if (action === "loadAdminParserGroups") {
      invoke("loadAdminParserGroups");
    } else if (action === "restartParser") {
      invoke("restartParser");
    }
  });

  document.body.addEventListener("input", (e) => {
    const el = getActionTarget(e);
    if (!el) return;
    const action = el.dataset.action;
    if (action === "filterSheets") {
      invoke("filterSheets", el);
    }
  });

  document.body.addEventListener("change", (e) => {
    const el = getActionTarget(e);
    if (!el) return;
    const action = el.dataset.action;
    if (action === "onSheetToggle") {
      invoke("onSheetToggle", el);
    } else if (action === "filterSheets") {
      invoke("filterSheets", el);
    } else if (action === "onShowHiddenToggle") {
      invoke("onShowHiddenToggle", el);
    } else if (action === "onSpecToggle") {
      invoke("onSpecToggle", el, Number(el.dataset.idx));
    } else if (action === "renderMassGroupPreview") {
      invoke("renderMassGroupPreview", Number(el.dataset.gidx));
    } else if (action === "onRefSheetToggle") {
      invoke("onRefSheetToggle");
    } else if (action === "clearTerritoryPair") {
      const target = document.getElementById(el.dataset.target || "");
      if (target) (target as HTMLSelectElement).value = "";
    }
  });

  document.body.addEventListener("click", (e) => {
    const el = getActionTarget(e);
    if (!el) return;
    const action = el.dataset.action;
    if (action === "onSelectAllSheets") {
      invoke("onSelectAllSheets", Number(el.dataset.fileIdx));
    } else if (action === "toggleSheetPanel") {
      invoke("toggleSheetPanel", Number(el.dataset.fileIdx));
    } else if (action === "clearAdminLog") {
      invoke("clearAdminLog");
    } else if (action === "dualListMove") {
      const args = (el.dataset.args || "").split(",");
      invoke("dualListMove", args[0], args[1], args[2]);
    }
  });
}

function bindClick(action: string, handler?: () => void): void {
  document.querySelectorAll(`[data-action='${action}']`).forEach((el) => {
    el.addEventListener("click", handler || (() => invoke(action)));
  });
}
